import React, { useEffect, useState } from 'react';
import { HERO } from '../mock';
import { ArrowRight, ChevronDown } from 'lucide-react';
import BoatAnimation from './BoatAnimation';

export default function Hero() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 2400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setProgress(Math.floor(p * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative hero-bg vignette min-h-screen flex flex-col" id="top">
      <div className="absolute inset-0 crosshair-grid opacity-30" />
      <div className="absolute inset-0 scanline opacity-40" />

      {/* Drifting yacht atmosphere — the opening "night on the water" */}
      <BoatAnimation />

      {/* Top bar */}
      <div className="relative z-10 container-narrow flex items-center justify-between pt-8 pb-4 gap-8">
        <div className="font-sc text-xs gold-text tracking-[0.32em] whitespace-nowrap">BLACK AXE</div>
        <div className="hidden md:flex items-center gap-8 font-sc text-[11px] text-[color:var(--bx-bone)]/70 tracking-[0.32em]">
          <a href="#logline" className="hover:text-[color:var(--bx-gold)] transition-colors">LOGLINE</a>
          <a href="#synopsis" className="hover:text-[color:var(--bx-gold)] transition-colors">SYNOPSIS</a>
          <a href="#pandi" className="hover:text-[color:var(--bx-gold)] transition-colors">PANDI</a>
          <a href="#vision" className="hover:text-[color:var(--bx-gold)] transition-colors">VISION</a>
          <a href="#contact-cta" className="hover:text-[color:var(--bx-gold)] transition-colors">CONTACT</a>
        </div>
        <div className="font-mono text-[10px] text-[color:var(--bx-muted)]">EST. 2026</div>
      </div>

      <div className="relative z-10 container-narrow flex-1 grid lg:grid-cols-12 gap-12 items-center py-16">
        {/* LEFT — text */}
        <div className="lg:col-span-7">
          <div className="fade-in font-sc text-[11px] gold-text tracking-[0.4em] mb-6">{HERO.starring}</div>
          <div className="fade-in font-sc text-[10px] text-[color:var(--bx-muted)] tracking-[0.4em] mb-4">{HERO.inAndAs}</div>
          <h1 className="fade-in-delay font-title text-[14vw] md:text-[8vw] lg:text-[7vw] leading-[0.95] text-gold-orange relative inline-block">
            {HERO.title}
          </h1>
          <div className="fade-in-delay font-sc text-[10px] text-[color:var(--bx-muted)] tracking-[0.34em] mt-6 mb-8">{HERO.director}</div>
          <p className="fade-in-delay-2 lead-quote text-2xl md:text-3xl max-w-xl mb-8">{HERO.tagline}</p>
          <p className="fade-in-delay-2 font-sans text-[15px] leading-relaxed text-[color:var(--bx-bone)]/75 max-w-xl mb-10">{HERO.description}</p>

          <div className="fade-in-delay-3 flex items-center gap-4 mb-8">
            <span className="bullet-mark" />
            <span className="font-sc text-[11px] gold-text tracking-[0.4em]">{HERO.status}</span>
          </div>

          <div className="fade-in-delay-3 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollTo('contact-cta')} className="btn-solid-gold">
              TAKE THE SHOT <ArrowRight size={14} />
            </button>
            <button onClick={() => scrollTo('night')} className="btn-ghost-gold">
              SCROLL THE DECK <ChevronDown size={14} />
            </button>
          </div>
        </div>

        {/* RIGHT — poster */}
        <div className="lg:col-span-5 fade-in-delay-2">
          <div className="relative float-y">
            <div className="absolute -inset-4 bg-[radial-gradient(ellipse_at_center,rgba(200,161,74,0.22),transparent_70%)] blur-2xl" />
            <div className="relative border border-[color:var(--bx-gold)]/30 bg-[#0a0807] p-3">
              <img
                src={HERO.poster}
                alt="BLACK AXE Official Poster"
                className="w-full h-auto block"
                style={{ filter: 'saturate(1.0) contrast(1.0) brightness(1.0)' }}
              />
              <div className="flex items-center justify-between mt-3 px-1">
                <span className="font-sc text-[10px] gold-text tracking-[0.32em]">{HERO.posterMeta}</span>
                <span className="font-sc text-[10px] text-[color:var(--bx-muted)] tracking-[0.32em]">{HERO.posterTag}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip — progress */}
      <div className="relative z-10 container-narrow pb-10">
        <div className="flex items-center justify-between font-sc text-[10px] text-[color:var(--bx-muted)] tracking-[0.4em] mb-3">
          <span>INITIALIZING HOLOGRAM</span>
          <span>{progress}%</span>
        </div>
        <div className="h-px w-full bg-[color:var(--bx-gold)]/15 overflow-hidden">
          <div className="h-full bg-[color:var(--bx-gold)] shimmer" style={{ width: `${progress}%`, transition: 'width 100ms linear' }} />
        </div>
      </div>
    </section>
  );
}
